import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import destinations from '@/locales/destinations.json';
import { languageFlags, Languages } from '@/locales/i18n';

import getDestination from './helpers/get-destination';
import getSelectDestination from './helpers/select-destination';

export default function useDestination() {
  const { t, i18n } = useTranslation();

  const parseLng = useCallback(
    (lang: string | Languages): Languages => {
      if (Object.values(Languages).includes(lang as Languages)) {
        return Languages[lang as Languages];
      }
      const fallback = i18n.options.fallbackLng;
      if (Object.values(Languages).includes(fallback as Languages)) {
        return Languages[fallback as Languages];
      }
      return Languages['en-US'];
    },
    [i18n.options.fallbackLng]
  );

  const destinationsAsOptions = useMemo(
    () =>
      destinations
        .map(destination => ({
          value: destination.name,
          label: t(`destinations.${destination.iso}`),
          icon: destination.icon,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(getSelectDestination),
    [t]
  );

  const getFlagIcon = useCallback((input: string): string => {
    const defaultFlag = 'https://flagcdn.com/un.svg';
    const dest = getDestination(input);
    return dest?.icon || defaultFlag;
  }, []);

  const getLocateName = useCallback(
    (input: string): string => {
      const destination = getDestination(input);
      return destination ? t(`destinations.${destination.iso}`) : input;
    },
    [t]
  );

  const getLanguageFlagIcon = useCallback(
    (lang: string) => {
      const language: Languages = parseLng(lang);
      return getFlagIcon(languageFlags[language]);
    },
    [getFlagIcon, parseLng]
  );

  return {
    parseLng,
    destinationsAsOptions,
    getFlagIcon,
    getLocateName,
    getLanguageFlagIcon,
  };
}
